import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { BottomSheetActions } from '../../../../Reducer/BottomSheetReducer';
// [버튼] 하단 버튼 공통 컴포넌트
import {
  BottomBar,
  ButtonRow,
  PrimaryButton,
  DangerButton,
  SecondaryButton,
} from '../ui/BottomActionBar';
import Complete from '../ui/Complete';
import RejectBottomSheet from '../ui/RejectBottomSheet';
import DeletePermitBottomSheet from '../ui/DeletePermitBottomSheet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../../Assets/Icon/CustomIcon';
import { CommonActions } from '../../../../Reducer/CommonReducer';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import AccordionSection from '../ui/AccordionSection';
import PermitHeaderCard from '../ui/PermitHeaderCard';
import WorkTypeTabBar from '../ui/WorkTypeTabBar';
import WitnessStatusBanner from '../ui/WitnessStatusBanner';
import ConfirmationNotes from '../ui/ConfirmationNotes';
import WitnessWorkEndGuideBottomSheet from '../ui/WitnessWorkEndGuideBottomSheet';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import SignBottomSheet from '../../../../Components/Ui/SignBottomSheet';

function formatWitnessSignDate(value) {
  if (!value) return '';
  const parsed = dayjs(String(value).trim());
  return parsed.isValid() ? parsed.format('MM-DD HH:mm') : '';
}

export default function M_PER_NEW_02(props) {
  const {
    isWitnessMode = false,
    witnessChecksByTab = {},
    onWitnessCheck,
    checkRemarksByTab = {},
    onCheckRemarkChange,
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const bottomSheetState = useSelector((state) => state.bottomSheet);

  const { baseInfo } = useSelector((state) => state.permit);
  const { userInfo } = useSelector((state) => state.BaseInfo);

  const [currentTab, setCurrentTab] = useState('');
  const [openSections, setOpenSections] = useState({
    hazards: true,
    equips: true,
    preps: true,
  });

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  useEffect(() => {
    const details = baseInfo.dailies?.[0]?.details;

    if (details && details.length > 0) {
      const firstWorkItemId = String(details[0].workItemId);

      setCurrentTab(firstWorkItemId);
    } else {
      setCurrentTab('');
    }

    setOpenSections({
      hazards: true,
      equips: true,
      preps: true,
    });
  }, [baseInfo.pmCd, baseInfo.dailies]);

  useEffect(() => {
    if (!props.signFlowTabId || !props.signFlowTrigger) return;

    if (!props.isWorkEndSignFlow) {
      setCurrentTab(String(props.signFlowTabId));
    }
    props.signatureCanvas?.current?.clear();
    dispatch(
      BottomSheetActions.OpenBottomSheet({
        type: 'witnessSign',
      })
    );
    props.onSignFlowTriggered?.();
  }, [
    props.signFlowTabId,
    props.signFlowTrigger,
    props.isWorkEndSignFlow,
    dispatch,
    props.onSignFlowTriggered,
  ]);

  const witnessSignStatusMap = useMemo(() => {
    const details = baseInfo.dailies?.[0]?.details || [];
    const map = {};

    details.forEach((item) => {
      const workItemId = String(item.workItemId);
      map[workItemId] = Boolean(
        props.witnessSignFiles?.[workItemId] ||
        props.witnessSignUrls?.[workItemId] ||
        item.witnessSignPath
      );
    });

    return map;
  }, [baseInfo.dailies, props.witnessSignFiles, props.witnessSignUrls]);

  const currentWorkTypeNm = useMemo(() => {
    const details = baseInfo.dailies?.[0]?.details || [];
    return (
      details.find((item) => String(item.workItemId) === String(currentTab))
        ?.workTypeNm || ''
    );
  }, [baseInfo.dailies, currentTab]);

  const signingTabId =
    props.isWorkEndSignFlow && props.signFlowTabId
      ? String(props.signFlowTabId)
      : String(currentTab);

  const signingWorkTypeNm = useMemo(() => {
    const details = baseInfo.dailies?.[0]?.details || [];
    return (
      details.find((item) => String(item.workItemId) === signingTabId)
        ?.workTypeNm || ''
    );
  }, [baseInfo.dailies, signingTabId]);

  const signingDetail = baseInfo.detailMap?.[signingTabId] ?? null;

  const activeTabContent = useMemo(() => {
    if (!currentTab || !baseInfo.detailMap) {
      return { detail: null, risks: [], equips: [], preps: [] };
    }

    const detail = baseInfo.detailMap[currentTab];

    if (!detail) {
      return { detail: null, risks: [], equips: [], preps: [] };
    }

    return {
      detail,
      risks: detail.riskItems || [],
      equips: detail.equipItems || [],
      preps: detail.prepItems || [],
    };
  }, [currentTab, baseInfo.detailMap, baseInfo.pmCd]);

  const { detail, risks, equips, preps } = activeTabContent;
  const mst = baseInfo.mst || {};
  const currentDaily = baseInfo.dailies?.[0] || {};
  const dailyStatus = currentDaily.status || mst.status || '';
  const workDateOnly =
    currentDaily.workDate?.split(' ')[0] ||
    baseInfo.currentWorkDate ||
    baseInfo.dailies?.[0]?.workDate?.split(' ')[0] ||
    '';
  const endTimeDisplay =
    detail?.isExtended === 'Y' ? detail.extEndTime : detail?.endTime;
  const displayTime = detail ? `${detail.strTime} ~ ${endTimeDisplay}` : '-';

  const isTimeExpired = useMemo(() => {
    if (!workDateOnly || !endTimeDisplay) return false;
    const strTime = detail?.strTime || '';
    // endTime이 strTime보다 이전이면 자정을 넘긴 것 → 종료일은 다음날
    const isOverMidnight = strTime && endTimeDisplay < strTime;
    const endDateStr = isOverMidnight
      ? dayjs(workDateOnly).add(1, 'day').format('YYYY-MM-DD')
      : workDateOnly;
    const endDateTime = dayjs(`${endDateStr} ${endTimeDisplay}`);
    return dayjs().isAfter(endDateTime.add(1, 'hour'));
  }, [workDateOnly, endTimeDisplay, detail?.strTime]);

  const isWitnessLocked = isTimeExpired;
  const isAuthor = mst.createId === userInfo?.decUserId;
  const hasAnyDepSign =
    baseInfo.centerSigns?.some((s) => !!s.centerSignPath) ?? false;
  const hasSmSign = currentDaily.smSign === 'Y' || baseInfo.mst?.smSign === 'Y';
  const hasAnyApprovalSign = hasAnyDepSign || hasSmSign;

  const showApproveReject = ['결재요청', '승인요청'].includes(dailyStatus);
  const showExtendApprove = dailyStatus === '연장요청';
  const isRejectedStatus =
    dailyStatus === '반려' || baseInfo.mst?.status === '반려';
  const isWaitingStatus =
    dailyStatus === '대기' || baseInfo.mst?.status === '대기';
  const showEditDelete =
    (isRejectedStatus && isAuthor) ||
    (isWaitingStatus && isAuthor && !hasAnyApprovalSign);

  const getWitnessChecks = (section) =>
    witnessChecksByTab[currentTab]?.[section] ?? {};
  const isNormalReturn = mst.returnId === mst.smId;
  const isExtensionReturn =
    baseInfo.dailies?.some((daily) =>
      daily.details?.some((detail) => detail.extReturnYn === 'Y')
    ) || false;
  const extensionReturnDate = baseInfo.dailies
    ?.flatMap((daily) => daily.details || [])
    ?.find((detail) => detail.extReturnYn === 'Y')?.updateDate;

  const isEditable = useMemo(() => {
    if (!isWitnessMode || !detail) return false;
    if (String(mst.assignWitnessId) !== String(userInfo?.decUserId)) {
      return false;
    }
    if (!detail.witnessId) return true;
    return String(detail.witnessId) === String(userInfo?.decUserId);
  }, [detail, userInfo, mst.assignWitnessId, isWitnessMode]);

  const hasWitnessRecord = Boolean(
    detail?.witnessSignPath ||
      detail?.witnessStatus === '입회완료' ||
      detail?.checkRemark ||
      checkRemarksByTab[currentTab] ||
      (detail?.prepItems || []).some((p) => p.witnessCheck === '0') ||
      Object.keys(getWitnessChecks('preps')).length > 0
  );

  const showWitnessUi = isWitnessMode || hasWitnessRecord;
  const witnessSignUrl =
    props.witnessSignUrls?.[currentTab] || detail?.witnessSignPath || '';
  const hasWitnessSign = Boolean(witnessSignStatusMap[currentTab] || witnessSignUrl);
  const witnessNm =
    detail?.witnessNm || mst?.witnessNm || mst?.assignWitnessNm || '입회 담당자';
  const witnessPosition =
    detail?.witnessRank ||
    detail?.witnessPosition ||
    mst?.witnessRank ||
    mst?.witnessPosition ||
    mst?.assignWitnessRank ||
    mst?.assignWitnessPosition ||
    '';
  const witnessDepNm =
    detail?.witnessDepNm ||
    mst?.witnessDepNm ||
    mst?.assignWitnessDepNm ||
    '';
  const witnessSignDateDisplay = formatWitnessSignDate(
    props.witnessSignDatesByTab?.[currentTab] || detail?.witnessSignDate
  );
  const showWitnessInApproval = Boolean(mst.assignWitnessId || showWitnessUi);

  const handleWitnessSignClick = () => {
    if (isTimeExpired) {
      dispatch(
        showSnackbar({
          message:
            '작업 종료 후 입력 가능 시간이 지나 서명할 수 없습니다.',
          type: 'warning',
        })
      );
      return;
    }
    if (!isEditable) {
      dispatch(
        showSnackbar({
          message: `작업 수정 권한이 없습니다. (수정자: ${detail?.updateNm})`,
          type: 'error',
        })
      );
      return;
    }
    dispatch(
      BottomSheetActions.OpenBottomSheet({
        type: 'witnessSign',
      })
    );
  };

  const isAnyRejected = isNormalReturn || isExtensionReturn;

  if (props.isComplete && !isWitnessMode)
    return (
      <div className="h-full px-4">
        <Complete />
      </div>
    );

  return (
    <div className="font-body min-h-screen bg-[#f8f9fb] pb-30 text-[#191c1e] antialiased">
      <main className="mx-5 space-y-4 pb-20">
        {(isWitnessMode || hasWitnessRecord) && (
          <WitnessStatusBanner
            currentTab={currentTab}
            isExpired={isTimeExpired}
          />
        )}

        <section>
          <PermitHeaderCard
            mst={mst}
            status={dailyStatus}
            extReturnDetail={detail?.extReturnDetail}
          />
        </section>

        <WorkTypeTabBar
          details={baseInfo.dailies?.[0]?.details}
          currentTab={currentTab}
          onTabChange={setCurrentTab}
          witnessSignStatusMap={
            showWitnessUi ? witnessSignStatusMap : undefined
          }
        />

        <AnimatePresence mode="popLayout">
          <motion.div
            key={currentTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="space-y-6"
          >
            <section className="space-y-3">
              <div className="flex items-center gap-2 px-1">
                <span
                  className="material-symbols-outlined text-[#5ABD4F]"
                  style={{ fontVariationSettings: "'FILL' 1" }}
                >
                  <Icon.Info />
                </span>
                <h3 className="font-headline text-lg font-bold">작업 개요</h3>
              </div>
              <div className="space-y-5 rounded-2xl border border-[#becab7]/20 bg-white p-5 shadow-sm">
                <div>
                  <p className="mb-3 text-[11px] font-bold tracking-widest text-[#5ABD4F] uppercase">
                    신청자 정보
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: '소속', value: mst.reqDepNm },
                      { label: '직위', value: mst.reqRank },
                      { label: '성명', value: mst.reqNm },
                    ].map((item, i) => (
                      <div
                        key={i}
                        className="rounded-xl bg-[#f2f4f6] py-2.5 text-center"
                      >
                        <p className="mb-0.5 text-[9px] font-bold text-[#3f4a3b]">
                          {item.label}
                        </p>
                        <p className="text-xs font-bold text-[#191c1e]">
                          {item.value || '-'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3 pt-1">
                  <div>
                    <p className="mb-1 text-[11px] font-bold text-[#3f4a3b]">
                      작업 시간
                    </p>
                    <p className="text-sm font-semibold text-[#191c1e]">
                      {workDateOnly} {displayTime}
                    </p>
                  </div>
                </div>

                <div className="border-t border-dashed border-[#becab7]/30 pt-4">
                  <p className="mb-3 text-[11px] font-bold tracking-widest text-[#3f4a3b] uppercase">
                    업체 정보
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#3f4a3b]">업체명</span>
                      <span className="font-bold">{mst.partnerNm || '-'}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#3f4a3b]">책임자</span>
                      <span className="font-bold">
                        {mst.partnerManager || '-'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#3f4a3b]">전화번호</span>
                      <span className="font-bold text-[#5ABD4F]">
                        {mst.partnerTel || '-'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <AccordionSection
              title="유해위험요인"
              icon="warning"
              isOpen={openSections.hazards}
              onToggle={() => toggleSection('hazards')}
              data={risks}
              labelKey="riskNm"
              mode="display"
            />

            <AccordionSection
              title="안전조치 사항"
              icon="fact_check"
              isOpen={openSections.preps}
              onToggle={() => toggleSection('preps')}
              data={preps}
              labelKey="preparationNm"
              mode={showWitnessUi ? 'witness' : 'display'}
              locked={
                !isWitnessMode ||
                isWitnessLocked ||
                !isEditable
              }
              checks={getWitnessChecks('preps')}
              onCheck={(idx, val) => {
                if (isEditable) {
                  onWitnessCheck?.(currentTab, 'preps', idx, val);
                } else {
                  dispatch(
                    showSnackbar({
                      message: `수정 권한이 없습니다. (현재 수정자: ${detail?.updateNm || '타인'})`,
                      type: 'error',
                    })
                  );
                }
              }}
            />

            <section className="space-y-3">
              <div className="flex items-center gap-2 px-1 text-[#191c1e]">
                <SecurityIcon
                  className="text-[#5ABD4F]"
                  sx={{ fontSize: 24 }}
                />
                <h3 className="font-headline text-lg font-bold">
                  필수 안전보호구
                </h3>
              </div>
              <div className="flex flex-wrap gap-2 rounded-2xl border border-[#becab7]/20 bg-white p-5 shadow-sm">
                {equips.length > 0 ? (
                  equips.map((item, i) => (
                    <span
                      key={i}
                      className={`rounded-lg border px-3 py-2 text-xs font-bold transition-all ${
                        item.itemCheck === '0'
                          ? 'border-[#5ABD4F]/20 bg-[#5ABD4F]/10 text-[#5ABD4F]'
                          : 'border-slate-200 bg-slate-50 text-slate-400 opacity-50'
                      }`}
                    >
                      {item.workEquipNm}
                    </span>
                  ))
                ) : (
                  <p className="w-full text-center text-sm text-[#3f4a3b]/40 italic">
                    등록된 보호구 없음
                  </p>
                )}
              </div>
            </section>

            <section className="space-y-3">
              <div className="flex items-center gap-2 px-1 text-[#191c1e]">
                <span
                  className="material-symbols-outlined text-[#5ABD4F]"
                  style={{ fontVariationSettings: "'FILL' 1" }}
                >
                  <Icon.Notes />
                </span>
                <h3 className="font-headline text-lg font-bold">기타 사항</h3>
              </div>
              <div className="min-h-20 rounded-2xl border border-[#becab7]/20 bg-white p-5 shadow-sm">
                <p className="text-sm leading-relaxed font-semibold text-[#191c1e]">
                  {detail?.etcRemark || '등록된 기타 사항이 없습니다.'}
                </p>
              </div>
            </section>

            {showWitnessUi && (
              <ConfirmationNotes
                value={
                  checkRemarksByTab[currentTab] ?? detail?.checkRemark ?? ''
                }
                lockedByName={detail?.updateNm}
                isExpired={isTimeExpired}
                onChange={(value) => {
                  if (isEditable) {
                    onCheckRemarkChange?.(currentTab, value);
                  }
                }}
                disabled={!isWitnessMode || !isEditable || isWitnessLocked}
              />
            )}

            <section className="space-y-2 pb-4">
              <div className="flex items-center gap-2 px-1 text-[#191c1e]">
                <DrawOutlinedIcon
                  sx={{
                    color: '#5ABD4F',
                  }}
                />
                <h3 className="font-headline text-lg font-bold text-[#3f4a3b]">
                  결재 현황
                </h3>
              </div>

              <div className="space-y-4 rounded-2xl border border-[#becab7]/20 bg-[#f2f4f6]/50 p-5">
                <div className="space-y-4">
                  <p className="flex items-center gap-2 text-[11px] font-bold tracking-widest text-[#3f4a3b]/70 uppercase">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#5ABD4F]/50"></span>
                    중간 결재자
                  </p>
                  <div className="grid grid-cols-1 gap-3">
                    {baseInfo.centerSigns?.length > 0 ? (
                      baseInfo.centerSigns.map((app, idx) => {
                        const isDone = !!app.centerSignPath;
                        const isReturn =
                          !!mst.returnId && mst.returnId === app.centerId;

                        return (
                          <div
                            key={`center-${idx}`}
                            className={`flex items-center justify-between rounded-xl border p-3 ${
                              isReturn
                                ? 'border-red-100 bg-red-50/30'
                                : 'border-[#becab7]/5 bg-[#f2f4f6]'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={`flex h-10 w-10 items-center justify-center rounded-full ${
                                  isReturn
                                    ? 'bg-red-100 text-red-500/60'
                                    : isDone
                                      ? 'bg-[#5ABD4F]/5 text-[#5ABD4F]/50'
                                      : 'bg-[#e7e8ea]/50 text-[#3f4a3b]/20'
                                }`}
                              >
                                <span
                                  className="material-symbols-outlined text-xl"
                                  style={{
                                    fontVariationSettings:
                                      isDone || isReturn
                                        ? "'FILL' 1"
                                        : "'FILL' 0",
                                  }}
                                >
                                  {isReturn
                                    ? 'cancel'
                                    : isDone
                                      ? 'check_circle'
                                      : 'radio_button_unchecked'}
                                </span>
                              </div>

                              <div>
                                <p className="text-sm font-bold text-[#191c1e]/80">
                                  {app.centerName}
                                  <span className="text-2xs ml-1 font-medium text-[#3f4a3b]/70">
                                    {app.centerPosition}
                                  </span>
                                </p>
                                <p
                                  className={`text-2xs ${isReturn ? 'text-red-400' : 'text-[#3f4a3b]/60'}`}
                                >
                                  {isReturn
                                    ? `${mst.updateDate?.substring(5, 16)} 반려`
                                    : isDone
                                      ? `${app.updateDate?.substring(5, 16)} 완료`
                                      : '검토 대기 중'}
                                </p>
                              </div>
                            </div>

                            <span
                              className={`text-2xs rounded px-2 py-0.5 font-extrabold ${
                                isReturn
                                  ? 'bg-red-100 text-red-500/70'
                                  : isDone
                                    ? 'bg-[#5ABD4F]/5 text-[#5ABD4F]/60'
                                    : 'bg-[#edeef0] text-[#3f4a3b]/40'
                              }`}
                            >
                              {isReturn
                                ? '반려됨'
                                : isDone
                                  ? '승인완료'
                                  : '대기중'}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <p className="py-2 text-center text-[11px] text-[#3f4a3b]/40">
                        지정된 중간 결재자가 없습니다.
                      </p>
                    )}
                  </div>
                </div>

                <div className="border-t border-dashed border-[#becab7]/20"></div>

                <div className="space-y-4">
                  <p className="flex items-center gap-2 text-[11px] font-bold tracking-widest text-[#3f4a3b]/70 uppercase">
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${isAnyRejected ? 'bg-red-500' : 'bg-[#8b5000]/50'}`}
                    ></span>
                    최종 결재자
                  </p>
                  <div className="group relative overflow-hidden">
                    <div
                      className={`relative flex items-center justify-between rounded-xl border p-4 transition-colors ${
                        isAnyRejected
                          ? 'border-red-100 bg-red-50/30'
                          : 'border-[#becab7]/10 bg-white/50'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`flex h-12 w-12 items-center justify-center rounded-full transition-colors ${
                            isAnyRejected
                              ? 'bg-red-100 text-red-500/60'
                              : hasSmSign
                                ? 'bg-[#5ABD4F]/10 text-[#5ABD4F]'
                                : 'bg-[#edeef0] text-[#3f4a3b]/20'
                          }`}
                        >
                          <span className="material-symbols-outlined text-2xl">
                            {isAnyRejected
                              ? 'cancel'
                              : hasSmSign
                                ? 'check_circle'
                                : 'radio_button_unchecked'}
                          </span>
                        </div>

                        <div>
                          <p className="text-base font-extrabold text-[#191c1e]/80">
                            {mst.smNm}
                            {!isAnyRejected && (
                              <span className="text-2xs ml-1 font-medium text-[#3f4a3b]/70">
                                {mst.smPosition}
                              </span>
                            )}
                          </p>

                          {/* 승인/반려 시간 표시 영역 */}
                          <p
                            className={`text-2xs ${isAnyRejected ? 'font-bold text-red-400' : 'text-[#3f4a3b]/60'}`}
                          >
                            {isExtensionReturn
                              ? `${extensionReturnDate?.substring(5, 16)} 연장 반려`
                              : isNormalReturn
                                ? `${mst.updateDate?.substring(5, 16)} 반려`
                                : hasSmSign
                                  ? `${mst.smSignDate?.substring(5, 16)} 승인 완료`
                                  : '결재 대기 중'}
                          </p>
                        </div>
                      </div>

                      <div className="text-right">
                        <div
                          className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold transition-colors ${
                            isAnyRejected
                              ? 'bg-red-100 text-red-500/70'
                              : hasSmSign
                                ? 'bg-[#5ABD4F]/10 text-[#5ABD4F]'
                                : 'bg-[#edeef0]/50 text-[#3f4a3b]/50'
                          }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              isAnyRejected
                                ? 'bg-red-500'
                                : hasSmSign
                                  ? 'bg-[#5ABD4F]'
                                  : 'bg-[#3f4a3b]/20'
                            }`}
                          ></span>
                          {isExtensionReturn
                            ? '연장 반려'
                            : isNormalReturn
                              ? '반려됨'
                              : hasSmSign
                                ? '승인 완료'
                                : '결재 대기'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {showWitnessInApproval && (
                  <>
                    <div className="border-t border-dashed border-[#becab7]/20"></div>

                    <div className="space-y-4">
                      <p className="flex items-center gap-2 text-[11px] font-bold tracking-widest text-[#3f4a3b]/70 uppercase">
                        <span className="h-1.5 w-1.5 rounded-full bg-indigo-400/70"></span>
                        입회자
                      </p>
                      <div
                        className={`group relative overflow-hidden ${
                          isWitnessMode &&
                          !hasWitnessSign &&
                          isEditable &&
                          !isWitnessLocked
                            ? 'cursor-pointer active:opacity-90'
                            : ''
                        }`}
                        onClick={() => {
                          if (
                            isWitnessMode &&
                            !hasWitnessSign &&
                            isEditable &&
                            !isWitnessLocked
                          ) {
                            handleWitnessSignClick();
                          }
                        }}
                      >
                        <div
                          className={`relative flex items-center justify-between rounded-xl border p-4 transition-colors ${
                            hasWitnessSign
                              ? 'border-[#becab7]/10 bg-white/50'
                              : isWitnessMode &&
                                  isEditable &&
                                  !isWitnessLocked
                                ? 'border-indigo-100 bg-indigo-50/20'
                                : 'border-[#becab7]/10 bg-white/50'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div
                              className={`flex h-12 w-12 items-center justify-center rounded-full transition-colors ${
                                hasWitnessSign
                                  ? 'bg-[#5ABD4F]/10 text-[#5ABD4F]'
                                  : 'bg-[#edeef0] text-[#3f4a3b]/20'
                              }`}
                            >
                              <span
                                className="material-symbols-outlined text-2xl"
                                style={{
                                  fontVariationSettings: hasWitnessSign
                                    ? "'FILL' 1"
                                    : "'FILL' 0",
                                }}
                              >
                                {hasWitnessSign ? 'check_circle' : 'draw'}
                              </span>
                            </div>

                            <div>
                              <p className="text-base font-extrabold text-[#191c1e]/80">
                                {witnessNm}
                                {witnessPosition && (
                                  <span className="text-2xs ml-1 font-medium text-[#3f4a3b]/70">
                                    {witnessPosition}
                                  </span>
                                )}
                              </p>
                              <p className="text-2xs text-[#3f4a3b]/60">
                                {witnessDepNm && (
                                  <span className="block">{witnessDepNm}</span>
                                )}
                                {hasWitnessSign
                                  ? `${witnessSignDateDisplay || '-'} 입회 완료`
                                  : isWitnessMode &&
                                      isEditable &&
                                      !isWitnessLocked
                                    ? '카드를 눌러 서명해 주세요'
                                    : '입회 대기 중'}
                              </p>
                            </div>
                          </div>

                          <div className="text-right">
                            <div
                              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold transition-colors ${
                                hasWitnessSign
                                  ? 'bg-[#5ABD4F]/10 text-[#5ABD4F]'
                                  : isWitnessMode &&
                                      isEditable &&
                                      !isWitnessLocked
                                    ? 'bg-indigo-100 text-indigo-600'
                                    : 'bg-[#edeef0]/50 text-[#3f4a3b]/50'
                              }`}
                            >
                              <span
                                className={`h-1.5 w-1.5 rounded-full ${
                                  hasWitnessSign
                                    ? 'bg-[#5ABD4F]'
                                    : 'bg-[#3f4a3b]/20'
                                }`}
                              ></span>
                              {hasWitnessSign
                                ? '입회 완료'
                                : isWitnessMode &&
                                    isEditable &&
                                    !isWitnessLocked
                                  ? '서명하기'
                                  : '입회 대기'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>
          </motion.div>
        </AnimatePresence>
      </main>

      {/* [버튼] */}
      <BottomBar variant="gradient">
        <div className="space-y-3">
          {isWitnessMode ? (
            <ButtonRow>
              <SecondaryButton onClick={() => props.WitnessTempSave?.()}>
                임시저장
              </SecondaryButton>
              <PrimaryButton
                flex="flex-[2]"
                onClick={() => {
                  props.WitnessWorkEnd?.();
                }}
                icon={<Icon.TaskAlt width="20" height="20" color="white" />}
              >
                작업종료
              </PrimaryButton>
            </ButtonRow>
          ) : (
            <>
              {showApproveReject && (
                <ButtonRow>
                  <DangerButton
                    onClick={() =>
                      dispatch(
                        BottomSheetActions.OpenBottomSheet({ type: 'reject' })
                      )
                    }
                  >
                    반려
                  </DangerButton>
                  <PrimaryButton
                    flex="flex-2"
                    onClick={() =>
                      dispatch(
                        BottomSheetActions.OpenBottomSheet({ type: 'sign' })
                      )
                    }
                  >
                    승인
                  </PrimaryButton>
                </ButtonRow>
              )}
              {showExtendApprove && (
                <ButtonRow>
                  <DangerButton
                    onClick={() =>
                      dispatch(
                        BottomSheetActions.OpenBottomSheet({
                          type: 'extendReject',
                        })
                      )
                    }
                  >
                    연장 반려
                  </DangerButton>
                  <PrimaryButton
                    flex="flex-2"
                    onClick={() =>
                      dispatch(
                        BottomSheetActions.OpenBottomSheet({
                          type: 'extendSign',
                        })
                      )
                    }
                  >
                    연장 승인
                  </PrimaryButton>
                </ButtonRow>
              )}
              {showEditDelete && (
                <ButtonRow>
                  <DangerButton
                    onClick={() =>
                      dispatch(
                        BottomSheetActions.OpenBottomSheet({ type: 'delete' })
                      )
                    }
                  >
                    삭제
                  </DangerButton>
                  <PrimaryButton
                    flex="flex-2"
                    onClick={() => {
                      dispatch(CommonActions.setActiveStep(1));
                      const dailyCd = String(
                        baseInfo.currentDailyCd ??
                          baseInfo.dailies?.[0]?.dailyCd ??
                          ''
                      );
                      navigate(`/M_Permit/write/${baseInfo.mst?.pmCd}`, {
                        state: { dailyCd },
                      });
                    }}
                  >
                    수정
                  </PrimaryButton>
                </ButtonRow>
              )}
            </>
          )}
        </div>
      </BottomBar>

      {/* 바텀시트 연동 */}
      {bottomSheetState.type === 'reject' && (
        <RejectBottomSheet Save1={props.Save1} />
      )}
      {bottomSheetState.type === 'extendReject' && (
        <RejectBottomSheet
          Save1={({ returnDetail }) =>
            props.Save6?.({
              gubun: '3',
              returnReason: returnDetail,
              dtlCds: props.extendedDtlCds ?? [],
            })
          }
          title="연장 반려"
          label="연장 반려 사유를 입력하세요"
        />
      )}
      {bottomSheetState.type === 'sign' && (
        <SignBottomSheet
          signMode="permitNew"
          signatureCanvas={props.signatureCanvas}
          Save1={props.Save1}
          onComplete={() => props.setIsComplete(true)}
        />
      )}
      {bottomSheetState.type === 'extendSign' && (
        <SignBottomSheet
          signMode="permitNew"
          signatureCanvas={props.signatureCanvas}
          Save1={({ signFile }) =>
            props.Save6?.({
              gubun: '2',
              signFile,
              dtlCds: props.extendedDtlCds ?? [],
            })
          }
          onComplete={() => {}}
        />
      )}
      {bottomSheetState.type === 'delete' && (
        <DeletePermitBottomSheet
          Delete1={props.Delete1}
          title="작업허가서를 삭제하시겠습니까?"
        />
      )}
      {bottomSheetState.type === 'witnessWorkEndGuide' && (
        <WitnessWorkEndGuideBottomSheet
          unsignedWorkTypes={props.unsignedForWorkEnd || []}
          onStartSign={() =>
            props.startWorkEndSignFlow?.(props.unsignedForWorkEnd)
          }
        />
      )}
      {bottomSheetState.type === 'witnessSign' && (
        <SignBottomSheet
          key={
            props.isWorkEndSignFlow
              ? 'witness-work-end-sign'
              : `witness-sign-${currentTab}`
          }
          signMode="witness"
          workTypeNm={
            props.isWorkEndSignFlow ? signingWorkTypeNm : currentWorkTypeNm
          }
          isWorkEndFlow={props.isWorkEndSignFlow}
          signatureCanvas={props.signatureCanvas}
          Save1={({ signFile, signUrl }) =>
            props.WitnessSave1({
              signFile,
              signUrl,
              tabId: signingTabId,
            })
          }
          initialImageUrl={
            props.isWorkEndSignFlow
              ? signingDetail?.witnessSignPath || ''
              : props.witnessSignUrls?.[currentTab] ||
                detail?.witnessSignPath ||
                ''
          }
          onDismiss={() => {
            props.onWitnessSignDismiss?.();
          }}
          onWorkEndStepComplete={props.onWorkEndSignStepComplete}
          onComplete={() => {
            if (!props.isWorkEndSignFlow) {
              dispatch(
                showSnackbar({ message: '서명되었습니다.', type: 'success' })
              );
              props.resetSignFlow?.();
            }
          }}
        />
      )}
    </div>
  );
}
